import React, { useState, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from 'semantic-ui-react';
import { Icon } from '@plone/volto/components';
import { getQueryStringResults } from '@plone/volto/actions';
import mapPinSVG from '@package/icons/map-pin.svg';
import listSVG from '@package/icons/list.svg';

import SearchForm from './SearchForm';
import Map from './Map';
import List from './List';

const messages = defineMessages({
  title: {
    id: 'search-prj-title',
    defaultMessage: 'Scopri i progetti',
  },
  subtitle: {
    id: 'search-prj-subtitle',
    defaultMessage: 'Usa i filtri per affinare la ricerca.',
  },
  results_found: {
    id: 'search-prj-results_found',
    defaultMessage: '{total} progetti trovati',
  },
  tablist_label: {
    id: 'search-prj-tablist',
    defaultMessage: 'Visualizza i risultati come',
  },
  map: {
    id: 'search-prj-map',
    defaultMessage: 'Mappa',
  },
  list: {
    id: 'search-prj-list',
    defaultMessage: 'Lista dei progetti',
  },
  no_results: {
    id: 'search-prj-no_results',
    defaultMessage: 'Nessun risultato trovato.',
  },
  loading: {
    id: 'search-prj-loading',
    defaultMessage: 'Loading...',
  },
});

const DEFAULT_FILTERS = { sort_on: 'titolo_bando' };
const Body = ({ isEditMode, id }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const results_region_id = id + 'src-prj-result';
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [view, setView] = useState('list');
  const querystringResults = useSelector((state) => {
    return state.querystringsearch?.subrequests?.[id] ?? {};
  });

  useEffect(() => {
    doSearch();
  }, [filters]);

  const doSearch = () => {
    setPage(1);
    let query = [
      {
        i: 'portal_type',
        o: 'plone.app.querystring.operation.selection.any',
        v: ['Progetto'],
      },
    ];

    //user filters
    const STRING_FILTERS = [
      'titolo_bando',
      'organizzazione_po',
      'veste_giuridica_capofila',
      'provincia',
      'stato_progetto',
    ];
    STRING_FILTERS.forEach((f) => {
      if (filters[f]?.length > 0) {
        query.push({
          i: f,
          o: 'plone.app.querystring.operation.string.is',
          v: filters[f],
        });
      }
    });

    const ARRAY_FILTERS = [
      'tipologie_intervento',
      'partenariato',
      'Subject',
      'anno_bando',
    ];
    ARRAY_FILTERS.forEach((f) => {
      if (filters[f]?.length > 0) {
        query.push({
          i: f,
          o: 'plone.app.querystring.operation.selection.any',
          v: [filters[f]],
        });
      }
    });

    const RANGE_FILTERS = [
      'budget_iniziale_progetto',
      'contributo_deliberato_fc',
    ];
    RANGE_FILTERS.forEach((f) => {
      const v = filters[f];
      if (v && v.range.min && v.range.max) {
        query.push({
          i: f,
          o: 'plone.app.querystring.operation.int.largerThan',
          v: v.range.min,
        });
        query.push({
          i: f,
          o: 'plone.app.querystring.operation.int.lessThan',
          v: v.range.max,
        });
        // query.push({
        //   i: f,
        //   o: 'plone.app.querystring.operation.int.between',
        //   v: [v.range.min, v.range.max],
        // });
      } else if (v && v.range.max) {
        query.push({
          i: f,
          o: 'plone.app.querystring.operation.int.lessThan',
          v: v.range.max,
        });
      } else if (v && v.range.min) {
        query.push({
          i: f,
          o: 'plone.app.querystring.operation.int.largerThan',
          v: v.range.min,
        });
      }
    });

    dispatch(
      getQueryStringResults(
        '',
        {
          metadata_fields: [
            'titolo_bando',
            'organizzazione_po',
            'contributo_deliberato_fc',
          ], //'_all',
          query: query,
          b_size: 999,
          sort_on: filters.sort_on,
          sort_order: 'ascending',
        },
        id,
        1, //currentPage,
      ),
    );
  };

  const ResultMessages = ({ q, children }) => {
    return q.loading ? (
      <Container>{intl.formatMessage(messages.loading)}</Container>
    ) : q.loaded ? (
      <>
        {q.items?.length === 0 && (
          <Container>{intl.formatMessage(messages.no_results)}</Container>
        )}
        {children}
      </>
    ) : (
      <></>
    );
  };

  return (
    <div className="search-prj-wrapper">
      <div className="search-prj-header">
        <h3>{intl.formatMessage(messages.title)}</h3>
        <div className="description">
          {intl.formatMessage(messages.subtitle)}
        </div>
      </div>

      <SearchForm
        filters={filters}
        setFilters={setFilters}
        results_region_id={results_region_id}
        reset={() => {
          setFilters(DEFAULT_FILTERS);
        }}
      />

      {/*----- RISULTATI ----- */}
      <div className="serach-prj-results full-width" id={results_region_id}>
        <div className="serach-prj-results-header">
          <Container>
            <div className="serach-prj-results-header-content">
              <div className="left">
                <div
                  role="tablist"
                  aria-label={intl.formatMessage(messages.tablist_label)}
                >
                  <button
                    id="tab-map"
                    type="button"
                    role="tab"
                    aria-selected={view === 'map'}
                    aria-controls={results_region_id + '_map'}
                    tabIndex="-1"
                    onClick={() => {
                      setView('map');
                    }}
                  >
                    <Icon name={mapPinSVG} />{' '}
                    <span>{intl.formatMessage(messages.map)}</span>
                  </button>
                  <button
                    id="tab-list"
                    type="button"
                    role="tab"
                    aria-selected={view === 'list'}
                    aria-controls={results_region_id + '_list'}
                    tabIndex="-1"
                    onClick={() => {
                      setView('list');
                    }}
                  >
                    <Icon name={listSVG} />{' '}
                    <span>{intl.formatMessage(messages.list)}</span>
                  </button>
                </div>
              </div>
              <div className="right">
                {intl.formatMessage(messages.results_found, {
                  total: querystringResults.total,
                })}
              </div>
            </div>
          </Container>
        </div>
        <div
          id={results_region_id + '_map'}
          role="tabpanel"
          tabIndex="0"
          aria-labelledby="tab-map"
          className={view !== 'map' ? 'hidden' : ''}
        >
          <ResultMessages q={querystringResults}>
            <Map items={querystringResults.items} />
          </ResultMessages>
        </div>
        <div
          id={results_region_id + '_list'}
          role="tabpanel"
          tabIndex="0"
          aria-labelledby="tab-list"
          className={view !== 'list' ? 'hidden' : ''}
        >
          <ResultMessages q={querystringResults}>
            <List
              visible={true} //{view === 'list'}
              filters={filters}
              setFilters={setFilters}
              results_region_id={results_region_id}
              items={querystringResults?.items}
              isEditMode={isEditMode}
              page={page}
              setPage={setPage}
            />
          </ResultMessages>
        </div>
      </div>
    </div>
  );
};

export default Body;
